import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Box,
  Typography,
  TextField,
  OutlinedInput,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Button,
  CircularProgress,
  IconButton,
  Tooltip,
  Avatar,
} from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SendIcon from "@mui/icons-material/Send";
import { fmtK } from "../utils";
import { planUnitsMonthly } from "../planMonthly";
import { catalogPlanNoteService } from "../../../utils/services/catalogPlanNoteService";
import { APP_LOCALE } from "../../../utils/formatDate";

// Deterministic avatar color from a string (user id or name)
const AVATAR_COLORS = [
  "#7b1fa2", // purple
  "#1565c0", // blue
  "#2e7d32", // green
  "#c62828", // red
  "#e65100", // orange
  "#00695c", // teal
  "#4527a0", // deep purple
  "#283593", // indigo
];
function avatarColor(seed) {
  if (!seed) return AVATAR_COLORS[0];
  let hash = 0;
  for (let i = 0; i < seed.length; i++)
    hash = seed.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

const MONTH_ABBR = [
  "JAN",
  "FEB",
  "MAR",
  "APR",
  "MAY",
  "JUN",
  "JUL",
  "AUG",
  "SEP",
  "OCT",
  "NOV",
  "DEC",
];

// ── InlineMonthly ────────────────────────────────────────────────────────────
// Renders the inline monthly breakdown section inside a colSpan table cell.
// All "K" values are in $K for display; planValue and year_proj arrive in raw $.
export default function InlineMonthly({
  category,
  planValue, // raw dollars
  currentMonth, // 1–12
  currentYear,
  darkMode,
  planId,
  clientId,
  categoryId,
  forecastError = null,
  forecastPending = false,
  onPlanChange = null, // (categoryId, totalK: string) => void
  // Row's monthly plan, 12 raw $ (closed months = actuals) — from the planner
  planMonthly = null,
  closedMonth = null, // number of closed (ended) months; their plan is the actual
  avgUnitPrice = null, // row's average unit price, for plan units
  onPlanMonthlyChange = null, // (categoryId, monthlyRaw: number[12]) => void
  readOnly = false, // a channel's view: no month is editable
  // This row's share of the latest finished plan forecast run:
  // { units: [12], revenue: [12], started_at, stale } — null = no run yet
  runForecast = null,
  showActual = true,
  showForecast = true,
}) {
  const [notesList, setNotesList] = useState([]);
  const [notesLoading, setNotesLoading] = useState(false);
  const [draftNote, setDraftNote] = useState("");
  const [noteSubmitting, setNoteSubmitting] = useState(false);
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editingText, setEditingText] = useState("");

  const fetchNotes = useCallback(async () => {
    if (!planId || !categoryId) return;
    setNotesLoading(true);
    try {
      const res = await catalogPlanNoteService.fetchByCategory(
        planId,
        categoryId,
      );
      setNotesList(res?.data || []);
    } catch (err) {
      console.error("Fetch category notes error:", err);
    } finally {
      setNotesLoading(false);
    }
  }, [planId, categoryId]);

  useEffect(() => {
    fetchNotes();
  }, [fetchNotes]);

  const handleAddNote = async () => {
    const text = draftNote.trim();
    if (!text || !planId || !categoryId) return;
    setNoteSubmitting(true);
    try {
      const created = await catalogPlanNoteService.create({
        plan_id: planId,
        client_id: clientId,
        category_id: categoryId,
        note: text,
      });
      setNotesList((prev) => [created, ...prev]);
      setDraftNote("");
    } catch (err) {
      console.error("Add category note error:", err);
    } finally {
      setNoteSubmitting(false);
    }
  };

  const handleDeleteNote = async (noteId) => {
    try {
      await catalogPlanNoteService.delete(noteId);
      setNotesList((prev) => prev.filter((n) => n._id !== noteId));
    } catch (err) {
      console.error("Delete category note error:", err);
    }
  };

  const handleEditStart = (note) => {
    setEditingNoteId(note._id);
    setEditingText(note.note);
  };

  const handleEditCancel = () => {
    setEditingNoteId(null);
    setEditingText("");
  };

  const handleEditSave = async (noteId) => {
    if (!editingText.trim()) return;
    try {
      const updated = await catalogPlanNoteService.update(noteId, {
        note: editingText.trim(),
      });
      setNotesList((prev) =>
        prev.map((n) => (n._id === noteId ? { ...n, note: updated.note } : n)),
      );
      setEditingNoteId(null);
      setEditingText("");
    } catch (err) {
      console.error("Edit category note error:", err);
    }
  };

  const lyMonthly = category.trend || Array(12).fill(0); // raw $
  const ytdMonthly = category.ytd_monthly || Array(12).fill(0); // raw $

  // ── Plan by month: from the planner (closed months = actuals), else an
  // LY-seasonality split of the annual plan ────────────────────────────────
  const planKTotal = Math.round(planValue / 1000);
  const lyTotal = lyMonthly.reduce((s, v) => s + (v || 0), 0);
  const planMonthlyK = planMonthly
    ? planMonthly.map((v) => Math.round((v || 0) / 1000))
    : lyTotal > 0
      ? lyMonthly.map((v) => Math.round(((v || 0) / lyTotal) * planKTotal))
      : Array(12).fill(Math.round(planKTotal / 12));
  // Months whose plan can't be edited (they show the actual).
  const lockedMonths = closedMonth ?? currentMonth;
  const planMonthlyKey = planMonthlyK.join(",");

  // ── Local editable plan (user can override per-month values) ────────────
  const [localPlanK, setLocalPlanK] = useState(() => [...planMonthlyK]);
  // Re-derive the monthly split from the LY seasonality whenever the category
  // OR its annual plan value changes (e.g. the total budget was edited) —
  // except when the change came from editing a month here (own onBlur).
  const selfEditRef = useRef(false);
  useEffect(() => {
    if (selfEditRef.current) {
      selfEditRef.current = false;
      return;
    }
    setLocalPlanK([...planMonthlyK]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryId, planValue, planMonthlyKey]);

  const localPlanTotal = localPlanK.reduce((s, v) => s + (v || 0), 0);
  const localPlanYtdK = localPlanK
    .slice(0, currentMonth)
    .reduce((s, v) => s + (v || 0), 0);

  const planYtdK = localPlanYtdK;

  // ── Actual (past months only) ────────────────────────────────────────────
  const actualMonthlyK = ytdMonthly.map((v, i) =>
    i + 1 <= currentMonth ? Math.round((v || 0) / 1000) : null,
  );
  const ytdActualK = actualMonthlyK
    .slice(0, currentMonth)
    .reduce((s, v) => s + (v || 0), 0);

  // ── Forecast: use API forecast_monthly for future months only ──────────────
  // forecast_monthly[i]: past months = actual (or null), future = API forecast
  const rawForecast = category.forecast_monthly || [];
  const forecastMonthlyK = Array.from({ length: 12 }, (_, i) => {
    if (i + 1 <= currentMonth) return null; // past months shown in ACTUAL row
    // A plan forecast run (new engine) wins over the planner's quick estimate.
    if (runForecast) {
      const v = runForecast.revenue?.[i];
      return v == null ? null : Math.round(v / 1000);
    }
    const apiVal = rawForecast[i];
    if (apiVal != null && apiVal > 0) return Math.round(apiVal / 1000);
    // Fallback: LY seasonality × remaining projection
    const yearProjK = Math.round((category.year_proj || 0) / 1000);
    const forecastRemainingK = Math.max(0, yearProjK - ytdActualK);
    const lyFutureTotal = lyMonthly
      .slice(currentMonth)
      .reduce((s, v) => s + (v || 0), 0);
    const lyVal = lyMonthly[i] || 0;
    return lyFutureTotal > 0
      ? Math.round((lyVal / lyFutureTotal) * forecastRemainingK)
      : Math.round(forecastRemainingK / Math.max(1, 12 - currentMonth));
  });

  const forecastYearK = forecastMonthlyK.reduce((s, v) => s + (v || 0), 0);

  // ── Δ vs Plan ────────────────────────────────────────────────────────────
  // Actual months: actual − plan. Open months: forecast − plan (the gap).
  const deltaMonthlyK = actualMonthlyK.map((v, i) => {
    const planK = localPlanK[i] ?? planMonthlyK[i];
    if (v !== null) return v - planK;
    return forecastMonthlyK[i] !== null ? forecastMonthlyK[i] - planK : null;
  });
  const deltaYtdK = deltaMonthlyK
    .slice(0, currentMonth)
    .reduce((s, v) => s + (v || 0), 0);
  // Year: what the rows above add up to — actual so far + forecast for the rest.
  const yearProjK = ytdActualK + forecastYearK;
  const deltaYearK = yearProjK - localPlanTotal;

  // ── Summary strip stats ──────────────────────────────────────────────────
  const ytdVsPlanPct =
    localPlanYtdK > 0
      ? ((ytdActualK - localPlanYtdK) / localPlanYtdK) * 100
      : null;
  const yearProjVsPlanPct =
    localPlanTotal > 0
      ? ((yearProjK - localPlanTotal) / localPlanTotal) * 100
      : null;

  // ── Cumulative plan (running monthly total) ──────────────────────────────────
  const cumulativePlanK = localPlanK.reduce((acc, v, i) => {
    acc.push((i > 0 ? acc[i - 1] : 0) + (v || 0));
    return acc;
  }, []);

  // ── Units (from backend ytd_monthly_units if available) ────────────────────
  const rawUnitsMonthly = category.ytd_monthly_units || [];
  const actualUnitsMonthly = Array.from({ length: 12 }, (_, i) =>
    i + 1 <= currentMonth && rawUnitsMonthly[i] != null
      ? Math.round(rawUnitsMonthly[i])
      : null,
  );
  const hasUnitsData = actualUnitsMonthly.some((v) => v !== null);
  const ytdActualUnits = actualUnitsMonthly.reduce((s, v) => s + (v || 0), 0);

  // ── Plan units: closed months = actual units; open = plan $ ÷ avg price ────
  const planUnits = avgUnitPrice
    ? planUnitsMonthly(
        localPlanK.map((k, i) =>
          i < lockedMonths && planMonthly ? planMonthly[i] : (k || 0) * 1000,
        ),
        rawUnitsMonthly,
        lockedMonths,
        avgUnitPrice,
      ).map((v) => (v == null ? null : Math.round(v)))
    : null;
  // Forecast units (from the run) and the units gap vs plan, open months only.
  const forecastUnits = runForecast
    ? Array.from({ length: 12 }, (_, i) => {
        if (i + 1 <= currentMonth) return null;
        const v = runForecast.units?.[i];
        return v == null ? null : Math.round(v);
      })
    : null;
  const forecastUnitsYear = forecastUnits
    ? forecastUnits.reduce((s, v) => s + (v || 0), 0)
    : null;
  const deltaUnits =
    forecastUnits && planUnits
      ? forecastUnits.map((v, i) =>
          v == null || planUnits[i] == null ? null : v - planUnits[i],
        )
      : null;

  const planUnitsYtd = planUnits
    ? planUnits.slice(0, currentMonth).reduce((s, v) => s + (v || 0), 0)
    : null;
  const planUnitsYear = planUnits
    ? planUnits.reduce((s, v) => s + (v || 0), 0)
    : null;

  // ── Colors ──────────────────────────────────────────────────────────────────────────
  const bg = darkMode ? "#1a1a1a" : "#fff";
  const subBg = darkMode ? "#1e1e1e" : "#fafafa";
  const borderColor = darkMode ? "#333" : "#e0e0e0";
  const textPrimary = darkMode ? "#fff" : "#111";
  const textSec = darkMode ? "#aaa" : "#666";
  const mutedColor = darkMode ? "#555" : "#bbb";

  const tCellSx = {
    py: 0.75,
    px: 1,
    fontSize: 12,
    borderBottom: `1px solid ${borderColor}`,
    whiteSpace: "nowrap",
  };

  // Format a value that is already in $K — shows K/M/B depending on magnitude
  const fmtKVal = (k) => {
    if (k == null || isNaN(k)) return "–";
    const abs = Math.abs(k);
    if (abs >= 1_000_000) {
      return `${(k / 1_000_000).toFixed(2)}B`;
    }
    if (abs >= 10_000) {
      return `${(k / 1_000).toFixed(2)}M`;
    }
    return `${k.toLocaleString()}K`;
  };

  const fmtCell = (v) =>
    v === null ? <span style={{ color: mutedColor }}>–</span> : fmtKVal(v);

  const renderDelta = (v) => {
    if (v === null) return <span style={{ color: mutedColor }}>–</span>;
    const color = v > 0 ? "#2e7d32" : v < 0 ? "#c62828" : textSec;
    const sign = v > 0 ? "+" : v < 0 ? "-" : "";
    return (
      <span style={{ color, fontWeight: 600 }}>
        {sign}
        {fmtKVal(Math.abs(v))}
      </span>
    );
  };

  const renderUnitsDelta = (v) => {
    const color = v > 0 ? "#2e7d32" : v < 0 ? "#c62828" : textSec;
    return (
      <span style={{ color, fontWeight: 600 }}>
        {v > 0 ? "+" : ""}
        {v.toLocaleString()}
      </span>
    );
  };

  const pctStr = (v) =>
    v == null ? null : `${v > 0 ? "+" : ""}${Math.round(v * 10) / 10}%`;

  return (
    <Box
      sx={{
        borderLeft: "3px solid #1976d2",
        bgcolor: bg,
        borderTop: `1px solid ${borderColor}`,
        px: 2.5,
        pt: 1.75,
        pb: 2,
      }}
    >
      {/* ── Header ── */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1.5,
          flexWrap: "wrap",
          gap: 1,
        }}
      >
        <Typography sx={{ fontSize: 14, fontWeight: 600, color: textPrimary }}>
          {category.name}
          {" · "}
          <span style={{ fontWeight: 400 }}>monthly breakdown</span>
        </Typography>

        {/* Right-side summary strip */}
        <Box
          sx={{
            display: "flex",
            gap: 2,
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <Typography sx={{ fontSize: 12, color: textSec }}>
            Plan{" "}
            <strong style={{ color: textPrimary }}>{fmtK(planValue)}</strong>
          </Typography>
          <Typography sx={{ fontSize: 12, color: textSec }}>
            YTD actual{" "}
            <strong style={{ color: "#1976d2" }}>
              {fmtK(ytdActualK * 1000)}
            </strong>
            {ytdVsPlanPct != null && (
              <span
                style={{
                  color: ytdVsPlanPct >= 0 ? "#2e7d32" : "#c62828",
                  marginLeft: 5,
                }}
              >
                {pctStr(ytdVsPlanPct)} vs plan
              </span>
            )}
          </Typography>
          <Typography sx={{ fontSize: 12, color: textSec }}>
            Year proj{" "}
            <strong style={{ color: textPrimary }}>
              {fmtK(yearProjK * 1000)}
            </strong>
            {yearProjVsPlanPct != null && (
              <span
                style={{
                  color: yearProjVsPlanPct >= 0 ? "#2e7d32" : "#c62828",
                  marginLeft: 5,
                }}
              >
                {pctStr(yearProjVsPlanPct)}
              </span>
            )}
          </Typography>
        </Box>
      </Box>

      {/* ── Notes & Comments ── */}
      <Typography
        sx={{
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: 0.6,
          textTransform: "uppercase",
          color: textSec,
          mb: 1,
        }}
      >
        Notes &amp; Comments
      </Typography>

      {/* Existing notes */}
      {!planId ? (
        <Typography sx={{ fontSize: 12, color: textSec, mb: 1.5 }}>
          Save the plan first to add notes.
        </Typography>
      ) : notesLoading ? (
        <Box
          sx={{ display: "flex", justifyContent: "center", py: 1.5, mb: 1.5 }}
        >
          <CircularProgress size={18} sx={{ color: "#1976d2" }} />
        </Box>
      ) : (
        <>
          {notesList.length > 0 && (
            <Box
              sx={{
                mb: 1.5,
                display: "flex",
                flexDirection: "column",
                gap: 1,
                maxHeight: 220,
                overflowY: "auto",
                pr: 0.5,
              }}
            >
              {notesList.map((n) => {
                const cb = n.created_by;
                const name =
                  cb?.name ||
                  [cb?.first_name, cb?.last_name].filter(Boolean).join(" ") ||
                  cb?.email ||
                  "";
                const initials = name
                  ? name
                      .split(" ")
                      .map((w) => w[0])
                      .join("")
                      .slice(0, 2)
                      .toUpperCase()
                  : "?";
                const bgColor = avatarColor(cb?._id?.toString() || name);
                const dateStr = n.createdAt
                  ? new Date(n.createdAt).toLocaleString(APP_LOCALE, {
                      month: "short",
                      day: "numeric",
                      year: "numeric",
                      hour: "numeric",
                      minute: "2-digit",
                    })
                  : "";

                return (
                  <Box
                    key={n._id}
                    sx={{ display: "flex", gap: 1.5, alignItems: "flex-start" }}
                  >
                    {/* Colored avatar */}
                    <Avatar
                      sx={{
                        width: 30,
                        height: 30,
                        fontSize: 11,
                        fontWeight: 700,
                        bgcolor: bgColor,
                        flexShrink: 0,
                        mt: 0.25,
                      }}
                    >
                      {initials}
                    </Avatar>

                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      {editingNoteId === n._id ? (
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 1,
                          }}
                        >
                          <TextField
                            size="small"
                            fullWidth
                            multiline
                            maxRows={4}
                            value={editingText}
                            onChange={(e) => setEditingText(e.target.value)}
                            autoFocus
                            sx={{
                              "& .MuiOutlinedInput-root": { fontSize: 12 },
                            }}
                          />
                          <Box
                            sx={{
                              display: "flex",
                              gap: 1,
                              justifyContent: "flex-end",
                            }}
                          >
                            <Button
                              size="small"
                              onClick={handleEditCancel}
                              sx={{
                                textTransform: "none",
                                fontSize: 11,
                                color: textSec,
                              }}
                            >
                              Cancel
                            </Button>
                            <Button
                              size="small"
                              variant="contained"
                              onClick={() => handleEditSave(n._id)}
                              disabled={!editingText.trim()}
                              sx={{
                                textTransform: "none",
                                fontSize: 11,
                                bgcolor: "#1976d2",
                                "&:hover": { bgcolor: "#1565c0" },
                              }}
                            >
                              Save
                            </Button>
                          </Box>
                        </Box>
                      ) : (
                        <>
                          {/* Name · date */}
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "baseline",
                              gap: 0.75,
                              mb: 0.3,
                            }}
                          >
                            {name && (
                              <Typography
                                sx={{
                                  fontSize: 12,
                                  fontWeight: 500,
                                  color: textPrimary,
                                }}
                              >
                                {name}
                              </Typography>
                            )}
                            {dateStr && (
                              <Typography sx={{ fontSize: 10, color: textSec }}>
                                {dateStr}
                              </Typography>
                            )}
                            {/* Edit / Delete — shown on hover via parent group */}
                            <Box sx={{ ml: "auto", display: "flex", gap: 0 }}>
                              <Tooltip title="Edit">
                                <IconButton
                                  size="small"
                                  onClick={() => handleEditStart(n)}
                                >
                                  <EditOutlinedIcon
                                    sx={{ fontSize: 13, color: textSec }}
                                  />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete">
                                <IconButton
                                  size="small"
                                  onClick={() => handleDeleteNote(n._id)}
                                >
                                  <DeleteOutlineIcon
                                    sx={{ fontSize: 13, color: "#c62828" }}
                                  />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </Box>
                          {/* Note text */}
                          <Typography
                            sx={{
                              fontSize: 12,
                              color: darkMode ? "#ccc" : "#444",
                              lineHeight: 1.55,
                            }}
                          >
                            {n.note}
                          </Typography>
                        </>
                      )}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}

          {/* Add note input + button */}
          <Box
            sx={{ display: "flex", gap: 1, alignItems: "flex-start", mb: 2 }}
          >
            <TextField
              multiline
              rows={2}
              fullWidth
              placeholder="Add a note or comment…"
              value={draftNote}
              onChange={(e) => setDraftNote(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && (e.ctrlKey || e.metaKey))
                  handleAddNote();
              }}
              variant="outlined"
              size="small"
              sx={{
                "& .MuiOutlinedInput-root": {
                  fontSize: 12,
                  bgcolor: darkMode ? "#242424" : "#fafafa",
                  "& fieldset": { borderColor },
                },
              }}
            />
            <Button
              variant="contained"
              size="small"
              onClick={handleAddNote}
              disabled={!draftNote.trim() || noteSubmitting}
              startIcon={
                noteSubmitting ? (
                  <CircularProgress size={12} color="inherit" />
                ) : (
                  <SendIcon sx={{ fontSize: 13 }} />
                )
              }
              sx={{
                mt: 0.5,
                fontSize: 12,
                whiteSpace: "nowrap",
                minWidth: 90,
                bgcolor: "#1976d2",
                "&:hover": { bgcolor: "#1565c0" },
              }}
            >
              Add
            </Button>
          </Box>
        </>
      )}

      {/* ── Monthly table ── */}
      <Box sx={{ overflowX: "auto" }}>
        <Table size="small" sx={{ minWidth: 920 }}>
          <TableHead>
            <TableRow>
              {/* Row label */}
              <TableCell
                sx={{
                  ...tCellSx,
                  width: 110,
                  bgcolor: subBg,
                  borderRight: `1px solid ${borderColor}`,
                }}
              />
              {MONTH_ABBR.map((m, i) => (
                <TableCell
                  key={m}
                  align="center"
                  sx={{
                    ...tCellSx,
                    bgcolor: subBg,
                    color: i + 1 > currentMonth ? mutedColor : textSec,
                    fontWeight: 700,
                    fontSize: 10,
                    letterSpacing: 0.4,
                  }}
                >
                  {m}
                </TableCell>
              ))}
              <TableCell
                align="center"
                sx={{
                  ...tCellSx,
                  // !important: the theme's table-head background otherwise wins
                  bgcolor: "#1976d2 !important",
                  color: "#fff",
                  fontWeight: 700,
                  fontSize: 10,
                  letterSpacing: 0.4,
                }}
              >
                YTD
              </TableCell>
              <TableCell
                align="center"
                sx={{
                  ...tCellSx,
                  bgcolor: "#1565c0 !important",
                  color: "#fff",
                  fontWeight: 700,
                  fontSize: 10,
                  letterSpacing: 0.4,
                }}
              >
                YEAR
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {/* PLAN ($K) */}
            <TableRow>
              <TableCell
                sx={{
                  ...tCellSx,
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: 0.5,
                  color: textSec,
                  textTransform: "uppercase",
                  bgcolor: subBg,
                  borderRight: `1px solid ${borderColor}`,
                  lineHeight: 1.3,
                }}
              >
                PLAN
                <br />
                <span style={{ fontSize: 9, fontWeight: 400 }}>($K)</span>
              </TableCell>
              {localPlanK.map((v, i) => {
                const isPast = readOnly || i < lockedMonths;
                return (
                  <TableCell
                    key={i}
                    align="center"
                    sx={{ ...tCellSx, p: 0.25 }}
                  >
                    {isPast ? (
                      <Typography
                        sx={{ fontSize: 11, color: mutedColor, py: 0.3 }}
                      >
                        {fmtCell(v)}
                      </Typography>
                    ) : (
                      <OutlinedInput
                        size="small"
                        value={String(v ?? 0)}
                        onChange={(e) => {
                          const num = parseFloat(e.target.value);
                          setLocalPlanK((prev) => {
                            const n = [...prev];
                            n[i] = isNaN(num) || num < 0 ? 0 : Math.round(num);
                            return n;
                          });
                        }}
                        onBlur={() => {
                          selfEditRef.current = true;
                          if (onPlanMonthlyChange && planMonthly) {
                            // Closed months keep their exact actuals.
                            onPlanMonthlyChange(
                              categoryId,
                              localPlanK.map((k, m) =>
                                m < lockedMonths
                                  ? planMonthly[m] || 0
                                  : (k || 0) * 1000,
                              ),
                            );
                            return;
                          }
                          const total = localPlanK.reduce(
                            (s, val) => s + (val || 0),
                            0,
                          );
                          onPlanChange?.(categoryId, String(total));
                        }}
                        sx={{
                          width: 56,
                          fontSize: 11,
                          "& .MuiOutlinedInput-input": {
                            py: 0.3,
                            px: 0.5,
                            textAlign: "center",
                          },
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: darkMode ? "#444" : "#ddd",
                          },
                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#1976d2",
                          },
                        }}
                      />
                    )}
                  </TableCell>
                );
              })}
              <TableCell
                align="center"
                sx={{ ...tCellSx, fontWeight: 500, color: "#1976d2" }}
              >
                {fmtKVal(localPlanYtdK)}
              </TableCell>
              <TableCell
                align="center"
                sx={{ ...tCellSx, fontWeight: 500, color: "#1565c0" }}
              >
                {fmtKVal(localPlanTotal)}
              </TableCell>
            </TableRow>

            {/* ACTUAL ($K) */}
            <TableRow sx={{ display: showActual ? "table-row" : "none" }}>
              <TableCell
                sx={{
                  ...tCellSx,
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: 0.5,
                  color: textSec,
                  textTransform: "uppercase",
                  bgcolor: subBg,
                  borderRight: `1px solid ${borderColor}`,
                  lineHeight: 1.3,
                }}
              >
                ACTUAL
                <br />
                <span style={{ fontSize: 9, fontWeight: 400 }}>($K)</span>
              </TableCell>
              {actualMonthlyK.map((v, i) => (
                <TableCell
                  key={i}
                  align="center"
                  sx={{
                    ...tCellSx,
                    color: textPrimary,
                  }}
                >
                  {fmtCell(v)}
                </TableCell>
              ))}
              <TableCell
                align="center"
                sx={{ ...tCellSx, fontWeight: 500, color: "#1976d2" }}
              >
                {fmtKVal(ytdActualK)}
              </TableCell>
              <TableCell align="center" sx={{ ...tCellSx, color: mutedColor }}>
                –
              </TableCell>
            </TableRow>

            {/* FORECAST ($K) */}
            <TableRow sx={{ display: showForecast ? "table-row" : "none" }}>
              <TableCell
                sx={{
                  ...tCellSx,
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: 0.5,
                  color: textSec,
                  textTransform: "uppercase",
                  bgcolor: subBg,
                  borderRight: `1px solid ${borderColor}`,
                  lineHeight: 1.3,
                }}
              >
                FORECAST
                <br />
                <span style={{ fontSize: 9, fontWeight: 400 }}>($K)</span>
                {runForecast && (
                  <span
                    title={`Forecast run of ${new Date(runForecast.started_at).toLocaleString()}${runForecast.stale ? " — stale" : ""}`}
                    style={{
                      display: "block",
                      fontSize: 8,
                      fontWeight: 400,
                      letterSpacing: 0,
                      color: runForecast.stale ? "#ed6c02" : "#888",
                    }}
                  >
                    {runForecast.stale ? "run · stale" : "from run"}
                  </span>
                )}
                {!runForecast && forecastPending && !forecastError && (
                  <span
                    style={{
                      display: "block",
                      fontSize: 8,
                      fontWeight: 400,
                      color: "#888",
                      letterSpacing: 0,
                    }}
                  >
                    estimating…
                  </span>
                )}
                {forecastError && (
                  <span
                    title={forecastError}
                    style={{
                      cursor: "help",
                      marginLeft: 2,
                      color: "#e57373",
                      fontSize: 10,
                    }}
                  >
                    ⚠
                  </span>
                )}
              </TableCell>
              {forecastMonthlyK.map((v, i) => (
                <TableCell
                  key={i}
                  align="center"
                  sx={{
                    ...tCellSx,
                    fontStyle: v !== null ? "italic" : "normal",
                    color: v !== null ? textPrimary : mutedColor,
                  }}
                >
                  {fmtCell(v)}
                </TableCell>
              ))}
              <TableCell align="center" sx={{ ...tCellSx, color: mutedColor }}>
                –
              </TableCell>
              <TableCell
                align="center"
                sx={{ ...tCellSx, fontWeight: 500, color: "#1565c0" }}
              >
                {fmtKVal(forecastYearK)}
              </TableCell>
            </TableRow>

            {/* Δ VS PLAN */}
            <TableRow sx={{ bgcolor: darkMode ? "#1e1e1e" : "#f9f9f9" }}>
              <TableCell
                sx={{
                  ...tCellSx,
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: 0.5,
                  color: textSec,
                  textTransform: "uppercase",
                  bgcolor: subBg,
                  borderRight: `1px solid ${borderColor}`,
                }}
              >
                Δ VS PLAN
              </TableCell>
              {deltaMonthlyK.map((v, i) => (
                <TableCell key={i} align="center" sx={{ ...tCellSx }}>
                  {renderDelta(v)}
                </TableCell>
              ))}
              <TableCell align="center" sx={{ ...tCellSx }}>
                {renderDelta(deltaYtdK)}
              </TableCell>
              <TableCell align="center" sx={{ ...tCellSx }}>
                {renderDelta(deltaYearK)}
              </TableCell>
            </TableRow>

            {/* SUM ($K) — cumulative running total of plan by month */}
            <TableRow>
              <TableCell
                sx={{
                  ...tCellSx,
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: 0.5,
                  color: textSec,
                  textTransform: "uppercase",
                  bgcolor: subBg,
                  borderRight: `1px solid ${borderColor}`,
                  lineHeight: 1.3,
                }}
              >
                SUM
                <br />
                <span style={{ fontSize: 9, fontWeight: 400 }}>
                  ($K cumul.)
                </span>
              </TableCell>
              {cumulativePlanK.map((v, i) => (
                <TableCell
                  key={i}
                  align="center"
                  sx={{ ...tCellSx, color: textPrimary }}
                >
                  {fmtKVal(v)}
                </TableCell>
              ))}
              <TableCell align="center" sx={{ ...tCellSx, color: mutedColor }}>
                –
              </TableCell>
              <TableCell
                align="center"
                sx={{ ...tCellSx, fontWeight: 500, color: "#1565c0" }}
              >
                {fmtKVal(localPlanTotal)}
              </TableCell>
            </TableRow>

            {/* PLAN UNITS — plan $ ÷ the row's average unit price */}
            {planUnits && (
              <TableRow>
                <TableCell
                  sx={{
                    ...tCellSx,
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: 0.5,
                    color: textSec,
                    textTransform: "uppercase",
                    bgcolor: subBg,
                    borderRight: `1px solid ${borderColor}`,
                    lineHeight: 1.3,
                  }}
                >
                  PLAN UNITS
                  <br />
                  <span style={{ fontSize: 9, fontWeight: 400 }}>
                    (@ ${Math.round(avgUnitPrice * 100) / 100})
                  </span>
                </TableCell>
                {planUnits.map((v, i) => (
                  <TableCell
                    key={i}
                    align="center"
                    sx={{
                      ...tCellSx,
                      color: i < lockedMonths ? mutedColor : textPrimary,
                    }}
                  >
                    {v !== null ? (
                      v.toLocaleString()
                    ) : (
                      <span style={{ color: mutedColor }}>–</span>
                    )}
                  </TableCell>
                ))}
                <TableCell
                  align="center"
                  sx={{ ...tCellSx, fontWeight: 500, color: "#1976d2" }}
                >
                  {planUnitsYtd.toLocaleString()}
                </TableCell>
                <TableCell
                  align="center"
                  sx={{ ...tCellSx, fontWeight: 500, color: "#1565c0" }}
                >
                  {planUnitsYear.toLocaleString()}
                </TableCell>
              </TableRow>
            )}

            {/* FORECAST UNITS + Δ UNITS — from the plan forecast run */}
            {forecastUnits && (
              <TableRow>
                <TableCell
                  sx={{
                    ...tCellSx,
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: 0.5,
                    color: textSec,
                    textTransform: "uppercase",
                    bgcolor: subBg,
                    borderRight: `1px solid ${borderColor}`,
                    lineHeight: 1.3,
                  }}
                >
                  FORECAST UNITS
                  <br />
                  <span style={{ fontSize: 9, fontWeight: 400 }}>(qty)</span>
                </TableCell>
                {forecastUnits.map((v, i) => (
                  <TableCell key={i} align="center" sx={{ ...tCellSx, color: textPrimary }}>
                    {v === null ? (
                      <span style={{ color: mutedColor }}>–</span>
                    ) : v.toLocaleString()}
                  </TableCell>
                ))}
                <TableCell align="center" sx={{ ...tCellSx, color: mutedColor }}>
                  –
                </TableCell>
                <TableCell align="center" sx={{ ...tCellSx, fontWeight: 500, color: "#1565c0" }}>
                  {forecastUnitsYear.toLocaleString()}
                </TableCell>
              </TableRow>
            )}
            {deltaUnits && (
              <TableRow>
                <TableCell
                  sx={{
                    ...tCellSx,
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: 0.5,
                    color: textSec,
                    textTransform: "uppercase",
                    bgcolor: subBg,
                    borderRight: `1px solid ${borderColor}`,
                    lineHeight: 1.3,
                  }}
                >
                  Δ UNITS
                  <br />
                  <span style={{ fontSize: 9, fontWeight: 400 }}>(vs plan)</span>
                </TableCell>
                {deltaUnits.map((v, i) => (
                  <TableCell key={i} align="center" sx={{ ...tCellSx, color: textPrimary }}>
                    {v === null ? (
                      <span style={{ color: mutedColor }}>–</span>
                    ) : renderUnitsDelta(v)}
                  </TableCell>
                ))}
                <TableCell align="center" sx={{ ...tCellSx, color: mutedColor }}>
                  –
                </TableCell>
                <TableCell align="center" sx={{ ...tCellSx, fontWeight: 500, color: "#1565c0" }}>
                  {renderUnitsDelta(deltaUnits.reduce((s, v) => s + (v || 0), 0))}
                </TableCell>
              </TableRow>
            )}

            {/* UNITS — actual unit quantities from backend if available */}
            <TableRow>
              <TableCell
                sx={{
                  ...tCellSx,
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: 0.5,
                  color: textSec,
                  textTransform: "uppercase",
                  bgcolor: subBg,
                  borderRight: `1px solid ${borderColor}`,
                  lineHeight: 1.3,
                }}
              >
                UNITS
                <br />
                <span style={{ fontSize: 9, fontWeight: 400 }}>(qty)</span>
              </TableCell>
              {actualUnitsMonthly.map((v, i) => (
                <TableCell
                  key={i}
                  align="center"
                  sx={{ ...tCellSx, color: textPrimary }}
                >
                  {v !== null ? (
                    v.toLocaleString()
                  ) : (
                    <span style={{ color: mutedColor }}>–</span>
                  )}
                </TableCell>
              ))}
              <TableCell
                align="center"
                sx={{ ...tCellSx, fontWeight: 500, color: "#1976d2" }}
              >
                {hasUnitsData ? (
                  ytdActualUnits.toLocaleString()
                ) : (
                  <span style={{ color: mutedColor }}>–</span>
                )}
              </TableCell>
              <TableCell align="center" sx={{ ...tCellSx, color: mutedColor }}>
                –
              </TableCell>
            </TableRow>

          </TableBody>
        </Table>
      </Box>

      {/* ── Footer ── */}
      <Typography
        sx={{ fontSize: 11, color: mutedColor, fontStyle: "italic", mt: 1 }}
      >
        Closed months show actuals · future months editable · changes
        rebalance unlocked rows
      </Typography>
    </Box>
  );
}
